import { useCallback, useState } from 'react'
import { apiClient } from '@/lib/api'
import { toast } from '@/hooks/use-toast'
import type { AirportBrief, RouteInfo } from '@/types/airTraffic'
import type { RouteLine } from './AirMap'
import { errorText } from './ui'

const code = (a: AirportBrief | null) => a?.iata || a?.icao || '?'

export function routeLabel(r: RouteInfo | null | undefined): string | null {
  if (!r || (!r.origin && !r.destination)) return null
  return `${code(r.origin)}→${code(r.destination)}`
}

export function routeLines(routes: Record<string, RouteInfo | null>): Record<string, RouteLine> {
  const pt = (a: AirportBrief | null): [number, number] | null => (a && a.lon != null && a.lat != null ? [a.lon, a.lat] : null)
  const out: Record<string, RouteLine> = {}
  for (const [hex, r] of Object.entries(routes)) {
    if (r) out[hex] = { origin: pt(r.origin), destination: pt(r.destination), ended: r.ended }
  }
  return out
}

/** Bulk FR24 routes for a list of aircraft (~1 credit each; repeats within 10 min are free). */
export default function useRoutes() {
  const [routes, setRoutes] = useState<Record<string, RouteInfo | null>>({})
  const [busy, setBusy] = useState(false)

  const load = useCallback(async (hexes: string[]) => {
    if (!hexes.length) return
    setBusy(true)
    try {
      const r = await apiClient.airRoutes(hexes.slice(0, 60))
      setRoutes((prev) => ({ ...prev, ...r.routes }))
      const found = Object.values(r.routes).filter(Boolean).length
      toast({
        title: `${found} of ${Object.keys(r.routes).length} routes found`,
        description: r.message || `${r.credits} FR24 credit${r.credits === 1 ? '' : 's'} used. Military and private flights are usually not on FR24.`,
      })
    } catch (e) {
      toast({ title: 'Routes unavailable', description: errorText(e), variant: 'destructive' })
    } finally {
      setBusy(false)
    }
  }, [])

  return { routes, busy, load }
}
